import * as RadixPopover from "@radix-ui/react-popover";
import { useContext, useRef, type ReactNode } from "react";

import { OverlayContentContext, useOverlayGrant } from "@/lib/overlay-gate";

/**
 * Radix `Popover`, styled to the system (flat by default — `DESIGN.md`
 * § Elevation & Depth reserves shadow for the dialog/sheet/search-overlay
 * trio, so this gets a hairline border and no shadow, the same
 * structure-from-space-and-hairlines rule every other non-modal surface in
 * the app follows). Registers with the shared overlay gate
 * (`lib/overlay-gate.ts`) exactly like `Dialog`/`Sheet` when standalone — a
 * popover triggered from ordinary page content cannot stack on a dialog, or
 * on another popover, any more than a second dialog could. A `Popover`
 * rendered *inside* an already-open `Dialog`/`Sheet`'s own content (Story
 * 15.3, `Combobox.tsx`'s listbox) is exempted from that same-slot
 * competition instead — see `OverlayContentContext`'s own doc comment
 * (`lib/overlay-gate.ts`) for why that is a different case, not a
 * loosening of the modal-on-modal rule itself.
 *
 * `SavedViewChips.tsx`'s "Save this view" flow is this component's first
 * standalone consumer (VC-2.6-05's live proof) — non-modal, anchored to the
 * button that opened it, dismissed by `Esc`, an outside click, or its own
 * Cancel, exactly what Radix's default dismiss behavior already gives it.
 * `Combobox.tsx` (Story 2.6) is the nested consumer above.
 */
export interface PopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: ReactNode;
  children: ReactNode;
  align?: "start" | "center" | "end";
  /**
   * Keeps DOM focus on the trigger instead of Radix's default of moving it
   * into the panel on open (and back on close) — `Combobox.tsx`'s
   * composition needs this so typing keeps working while the listbox is
   * open (the WAI-ARIA combobox pattern keeps focus on the input the whole
   * time; `aria-activedescendant` communicates the highlighted option).
   */
  preventAutoFocus?: boolean;
  /**
   * `false` (default): `trigger` is wrapped in Radix's own
   * `Popover.Trigger` — a plain launcher button whose click toggles
   * open/closed, the right shape for an ordinary popover ("Save this
   * view").
   *
   * `true`: `trigger` is wrapped in `Popover.Anchor` instead — a pure
   * positioning reference with no click behavior of its own.
   * `Combobox.tsx` needs this: its input already owns open/close through
   * its own focus and keyboard handling, and Radix's `Trigger` toggle
   * fires from the *same* click as that focus handling, racing it — found
   * live, twice: a real mouse click focused the input but never opened
   * the list (`aria-expanded` stayed `"false"`), because Radix's toggle
   * read the pre-focus `open` state and closed what the focus handler had
   * just (about to) open.
   *
   * Fixing that uncovered a second, deeper issue in the same spot (also
   * found live, by instrumenting the actual state transitions): even with
   * `Trigger` out of the way, Radix's `Content` runs its own
   * `DismissableLayer`, which treats *any* focus landing outside `Content`
   * as a dismiss signal — and the anchor is, by definition, outside
   * `Content` at the exact instant it receives the very focus event that
   * is supposed to open the popover (`Content` doesn't exist yet, or is
   * only just mounting). So focusing the anchor immediately dismissed the
   * popover it had just asked to open, every time, confirmed by walking
   * the actual call stack: `closeList` ← `onOpenChange` ← Radix's
   * `onDismiss` ← `HTMLDocument.handleFocus`. `onFocusOutside` below
   * ignores exactly this one case — a focus-outside event whose target is
   * inside the anchor this same `Popover` instance owns — while leaving
   * every other outside interaction (a real click elsewhere, `Esc`,
   * tabbing to a genuinely different field) dismissing normally.
   */
  asAnchor?: boolean;
}

export function Popover({
  open,
  onOpenChange,
  trigger,
  children,
  align = "start",
  preventAutoFocus = false,
  asAnchor = false,
}: PopoverProps) {
  //: Story 15.3 — a `Popover` rendered inside an already-open `Dialog`/
  //: `Sheet` (e.g. `Combobox`'s own listbox, `AddLeaseDialog.tsx`'s tenant
  //: picker) is that overlay's own visual child, not a second, independent
  //: overlay competing for the shared gate — `OverlayContentContext`'s own
  //: doc comment (`lib/overlay-gate.ts`) has the full reasoning. `use
  //: OverlayGrant` is still called unconditionally (Rules of Hooks) even
  //: when its result goes unused here — it always loses that real gate
  //: request while nested (the ancestor overlay already holds the slot),
  //: which is a harmless no-op: it never changes what the ancestor owns.
  //: A standalone `Popover` with no `Dialog`/`Sheet` ancestor (e.g.
  //: `SavedViewChips.tsx`'s "Save this view") reads `false` here and keeps
  //: going through the real gate exactly as before.
  const insideGrantedOverlay = useContext(OverlayContentContext);
  const gateGranted = useOverlayGrant(open);
  const granted = insideGrantedOverlay ? open : gateGranted;
  const anchorRef = useRef<HTMLDivElement>(null);

  return (
    <RadixPopover.Root open={granted} onOpenChange={onOpenChange}>
      {asAnchor ? (
        <RadixPopover.Anchor ref={anchorRef} asChild>
          {trigger}
        </RadixPopover.Anchor>
      ) : (
        <RadixPopover.Trigger asChild>{trigger}</RadixPopover.Trigger>
      )}
      <RadixPopover.Portal>
        <RadixPopover.Content
          align={align}
          sideOffset={8}
          onOpenAutoFocus={(event) => {
            if (preventAutoFocus) {
              event.preventDefault();
            }
          }}
          onCloseAutoFocus={(event) => {
            if (preventAutoFocus) {
              event.preventDefault();
            }
          }}
          onFocusOutside={(event) => {
            if (asAnchor && anchorRef.current?.contains(event.target as Node)) {
              event.preventDefault();
            }
          }}
          className="z-50 origin-[var(--radix-popper-transform-origin)] rounded-md border border-border-hairline bg-surface p-4 data-[state=closed]:animate-menu-out data-[state=open]:animate-menu-in"
        >
          {children}
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}
